import { useEffect, useRef, useState } from 'react';

/**
 * Picking a date in the calendar saves right away. Typing does too, but only
 * once the year is plausible: a date input fires a change per digit of the year
 * (0002, 0020, 0202, 2026), and each of those would otherwise be stored and
 * toasted. Clearing the field is saved when it is left — an empty value also
 * shows up in between while typing.
 *
 * Shared by the Einstellen list (erster Arbeitstag) and the Offboarding list
 * (the two stichtage).
 */
const VOLLSTAENDIGES_DATUM = /^(\d{4})-\d{2}-\d{2}$/;

function plausibel(value: string): boolean {
  const match = VOLLSTAENDIGES_DATUM.exec(value);
  return match !== null && Number(match[1]) >= 2000;
}

export function StichtagInput({
  field,
  label,
  saved,
  disabled,
  onSave,
}: {
  field: string;
  label: string;
  saved: string;
  disabled: boolean;
  onSave: (value: string) => void;
}) {
  const [value, setValue] = useState(saved);
  /** What was last sent, so a blur right after a save does not send it again. */
  const lastSent = useRef(saved);
  // Follow the stored value when it changes from outside (reset, refetch).
  useEffect(() => {
    setValue(saved);
    lastSent.current = saved;
  }, [saved]);

  const send = (next: string) => {
    if (next === lastSent.current) return;
    lastSent.current = next;
    onSave(next);
  };

  const handleChange = (next: string) => {
    setValue(next);
    if (plausibel(next)) send(next);
  };

  const commit = () => {
    if (value === '' || plausibel(value)) send(value);
  };

  return (
    <div>
      <label htmlFor={field} className="block text-sm font-medium text-gray-700">
        {label}
      </label>
      <input
        id={field}
        type="date"
        value={value}
        disabled={disabled}
        onChange={(e) => handleChange(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => e.key === 'Enter' && commit()}
        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm disabled:bg-gray-50 disabled:text-gray-500"
      />
    </div>
  );
}
